import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SearchableDropdown from '../components/SearchableDropdown';
import { ChevronLeft, Save, ChevronDown } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { classService } from '../services/classService';
import CustomDatePicker from '../components/ui/CustomDatePicker';
import { staffService } from '../services/staffService';
import { useSelector } from 'react-redux';
import APIRequest from '../api/APIRequest';

const ClassEdit = ({ classData: propClassData }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [classData, setClassData] = useState(propClassData);
  const [loading, setLoading] = useState(!propClassData);
  const [saving, setSaving] = useState(false);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const { user } = useSelector((state) => state.auth);
  const academicYearToRef = useRef(null);
  const [isTermsDropdownOpen, setIsTermsDropdownOpen] = useState(false);
  const termsDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (termsDropdownRef.current && !termsDropdownRef.current.contains(event.target)) {
        setIsTermsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    section: '',
    class_capacity: 0,
    feesPerAcademicYear: 0,
    numberOfTerms: 1,
    inchargeId: '',
    termFees: [{ amount: 0, dueDate: '' }],
    academicYearFrom: '',
    academicYearTo: ''
  });

  useEffect(() => {
    // If class data is passed as props, use it directly
    if (propClassData) {
      setClassData(propClassData);
      setFormData({
        name: propClassData.name,
        section: propClassData.section,
        class_capacity: propClassData.class_capacity,
        feesPerAcademicYear: propClassData.feesPerAcademicYear || 0,
        numberOfTerms: propClassData.numberOfTerms || 1,
        inchargeId: propClassData?.inchargeId || propClassData.classIncharge || '',
        termFees: propClassData.termFees || [{ amount: 0, dueDate: '' }],
        academicYearFrom: propClassData.academicYearFrom ? new Date(propClassData.academicYearFrom).toISOString().split('T')[0] : '',
        academicYearTo: propClassData.academicYearTo ? new Date(propClassData.academicYearTo).toISOString().split('T')[0] : ''
      });
      setLoading(false);
      return;
    }

    // Otherwise, fetch class data
    const fetchClassData = async () => {
      setLoading(true);
      try {
        // Find class by ID from service
        const foundClass = classService.getClassById(id);
        
        if (foundClass) {
          setClassData(foundClass);
          setFormData({
            name: foundClass.name,
            section: foundClass.section,
            class_capacity: foundClass.class_capacity,
            feesPerAcademicYear: foundClass.feesPerAcademicYear || 0,
            numberOfTerms: foundClass.numberOfTerms || 1,
            inchargeId: foundClass.inchargeId|| foundClass.classIncharge || '',
            termFees: foundClass.termFees || [{ amount: 0, dueDate: '' }],
            academicYearFrom: foundClass.academicYearFrom ? new Date(foundClass.academicYearFrom).toISOString().split('T')[0] : '',
            academicYearTo: foundClass.academicYearTo ? new Date(foundClass.academicYearTo).toISOString().split('T')[0] : ''
          });
        } else {
          // Handle case where class is not found
          navigate('/classes');
        }
      } catch (error) {
        console.error('Error fetching class data:', error);
        navigate('/classes');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchClassData();
    }
  }, [id, navigate, propClassData]);

  useEffect(() => {
    const fetchTeacherOptions = async () => {
      const teachers = await staffService.getAllTeachers(user.token);
      setTeacherOptions(teachers);
    };
    fetchTeacherOptions();
  }, [user.token]);

  const handleNumberOfTermsChange = (newNumberOfTerms) => {
    const feePerTerm = formData.feesPerAcademicYear > 0 ? 
      Math.round((formData.feesPerAcademicYear / newNumberOfTerms) * 100) / 100 : 0;
    
    const currentTermFees = [];
    
    // Create new arrays with the correct number of terms
    for (let i = 0; i < newNumberOfTerms; i++) {
      // Preserve existing data if available, otherwise use calculated values
      const existingTermFee = formData.termFees[i];
      currentTermFees.push({
        amount: existingTermFee ? existingTermFee.amount : feePerTerm,
        dueDate: existingTermFee ? existingTermFee.dueDate : ''
      });
    }
    
    setFormData({
      ...formData,
      numberOfTerms: newNumberOfTerms,
      termFees: currentTermFees
    });
  };

  const handleTermFeeChange = (termIndex, field, value) => {
    const updatedTermFees = [...formData.termFees];
    updatedTermFees[termIndex] = {
      ...updatedTermFees[termIndex],
      [field]: field === 'amount' ? Number(value) : value
    };
    
    // Calculate total fees from individual term fees
    const totalFees = updatedTermFees.reduce((sum, term) => sum + (term.amount || 0), 0);
    
    setFormData({
      ...formData,
      termFees: updatedTermFees,
      feesPerAcademicYear: totalFees
    });
  };

  const handleTotalFeesChange = (newTotalFees) => {
    const feePerTerm = newTotalFees > 0 && formData.numberOfTerms > 0 ? 
      Math.round((newTotalFees / formData.numberOfTerms) * 100) / 100 : 0;
    
    const updatedTermFees = formData.termFees.map(termFee => ({
      ...termFee,
      amount: feePerTerm
    }));
    
    setFormData({
      ...formData,
      feesPerAcademicYear: newTotalFees,
      termFees: updatedTermFees
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate academic year dates
    if (formData.academicYearFrom && formData.academicYearTo) {
      const fromDate = new Date(formData.academicYearFrom);
      const toDate = new Date(formData.academicYearTo);
      
      if (toDate < fromDate) {
        error('Academic Year To date must be greater than or equal to Academic Year From date.');
        return;
      }
    }
    
    // Validate all required fields
    if (!formData.name || !formData.section || !formData.class_capacity || 
        !formData.feesPerAcademicYear || !formData.numberOfTerms || 
        !formData.academicYearFrom || !formData.academicYearTo) {
      error('Please fill in all required fields.');
      return;
    }
    
    // Check for duplicate class with same academic year (excluding current class)
    const fromTime = new Date(formData.academicYearFrom).getTime();
    const toTime = new Date(formData.academicYearTo).getTime();
    
    const allClasses = await classService.getAllClasses(user.token);
    const duplicateClass = allClasses.find(cls => 
      cls._id !== id && // Exclude current class being edited
      cls.name === formData.name && 
      cls.section === formData.section && 
      cls.academicYearFrom === fromTime && 
      cls.academicYearTo === toTime
    );
    
    if (duplicateClass) {
      error(`Class ${formData.name} Section ${formData.section} already exists for this academic year.`);
      return;
    }
    
    setSaving(true);
    try {
      // Find the selected teacher object
      const selectedTeacher = teacherOptions.find(teacher => teacher._id === formData.inchargeId._id);
      const payload = {
        "name": formData.name,
        "section": formData.section,
        "class_capacity": formData.class_capacity,
        "feesPerAcademicYear": formData.feesPerAcademicYear,
        "numberOfTerms": formData.numberOfTerms,
        "termFees": formData.termFees,
        "academicYearFrom": formData.academicYearFrom ? new Date(formData.academicYearFrom).getTime() : null,
        "academicYearTo": formData.academicYearTo ? new Date(formData.academicYearTo).getTime() : null,
        "classIncharge": selectedTeacher ? {
          "_id": selectedTeacher._id,
          "name": selectedTeacher.name
        } : null,
      }
      const api = new APIRequest();
      const response = await api.request(`/admin/updateClass?id=${id}`, 'POST', payload, user.token);
      if (response.success) {
        success('Class updated successfully!');
        navigate(`/classes/${id}`);
      } else {
        error(response.message || 'Failed to update class. Please try again.');
      }
    } catch (err) {
      console.error('Error updating class:', err);
      error('Failed to update class. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Class Not Found</h2>
          <p className="text-gray-600 mb-4">The class you're trying to edit doesn't exist.</p>
          <button
            onClick={() => navigate('/classes')}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Back to Classes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between animate-slideInDown">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/classes')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Edit Class</h1>
            <p className="text-gray-600 mt-1">Update class information and assignments</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border animate-slideInUp flex flex-col h-[calc(100vh-200px)]">
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          {/* Scrollable Form Fields */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Class Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value.toUpperCase()})}
                  placeholder="e.g. GRADE 10"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm"
                  required
                />
              </div>
              <div>
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Section *</label>
                <input
                  type="text"
                  value={formData.section}
                  onChange={(e) => setFormData({...formData, section: e.target.value.toUpperCase()})}
                  placeholder="e.g. A"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Class Capacity *</label>
                <input
                  type="number"
                  value={formData.class_capacity}
                  onChange={(e) => setFormData({...formData, class_capacity: Number(e.target.value)})}
                  placeholder="Enter capacity"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  min="1"
                  required
                />
              </div>
                <div>
                  <CustomDatePicker
                    label="Academic Year From"
                    name="academicYearFrom"
                    value={formData.academicYearFrom}
                    onChange={(e) => {
                      setFormData({...formData, academicYearFrom: e.target.value});
                      setTimeout(() => academicYearToRef.current?.open(), 100);
                    }}
                    required={true}
                    title="Select Academic Year Start Date"
                  />
                  <p className="mt-1 text-xs text-gray-500">Select the start date of the academic year</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <CustomDatePicker
                    label="Academic Year To"
                    name="academicYearTo"
                    value={formData.academicYearTo}
                    onChange={(e) => setFormData({...formData, academicYearTo: e.target.value})}
                    required={true}
                    ref={academicYearToRef}
                    title="Select Academic Year End Date"
                  />
                  <p className="mt-1 text-xs text-gray-500">Select the end date of the academic year</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <SearchableDropdown
                  options={teacherOptions}
                  value={formData.inchargeId || ''}
                  onChange={(value) => setFormData({...formData, inchargeId: value})}
                  placeholder="Select Class Incharge"
                  label="Class Incharge"
                  searchPlaceholder="Search teachers..."
                  noResultsText="No teachers found"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Fees per Academic Year (₹) *</label>
                <input
                  type="number"
                  value={formData.feesPerAcademicYear}
                  onChange={(e) => handleTotalFeesChange(Number(e.target.value))}
                  placeholder="Enter total annual fees"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div>
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Number of Terms *</label>
                <div className="relative w-40" ref={termsDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsTermsDropdownOpen(!isTermsDropdownOpen)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl flex items-center justify-between bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 group shadow-sm text-sm"
                  >
                    <span className="font-bold text-slate-700">
                      {formData.numberOfTerms || 'Select'}
                    </span>
                    <ChevronDown 
                      size={18} 
                      className={`text-slate-400 transition-transform duration-300 ${isTermsDropdownOpen ? 'rotate-180 text-blue-500' : ''}`}
                    />
                  </button>

                  {isTermsDropdownOpen && (
                    <div className="absolute z-[100] w-full mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl shadow-blue-900/10 overflow-hidden animate-fadeIn backdrop-blur-xl">
                      <div className="max-h-[240px] overflow-y-auto custom-scrollbar p-1.5">
                        {[...Array(12)].map((_, i) => (
                          <button
                            key={i + 1}
                            type="button"
                            onClick={() => {
                              handleNumberOfTermsChange(i + 1);
                              setIsTermsDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm font-bold rounded-xl transition-all duration-200 mb-0.5 ${formData.numberOfTerms === (i + 1) ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                          >
                            {i + 1} {i + 1 === 1 ? 'Term' : 'Terms'}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Term-wise Fees and Due Dates */}
            {formData.numberOfTerms > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-3">Term-wise Fees and Due Dates</h4>
                <p className="text-xs text-blue-600 mb-3">💡 Term fees are automatically calculated when you change the total annual fees or number of terms. You can manually adjust individual term fees if needed.</p>
                <div className="space-y-4">
                  {formData.termFees.map((termFee, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-1">
                          Term {index + 1} Fee (₹) *
                        </label>
                        <input
                          type="number"
                          value={termFee.amount}
                          onChange={(e) => handleTermFeeChange(index, 'amount', e.target.value)}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div>
                        <CustomDatePicker
                          label={`Term ${index + 1} Due Date`}
                          name={`term_${index}_dueDate`}
                          value={termFee.dueDate}
                          onChange={(e) => handleTermFeeChange(index, 'dueDate', e.target.value)}
                          required={true}
                        />
                      </div>
                    </div>
                  ))}
                  <div className="border-t border-blue-200 pt-2 mt-2">
                    <div className="flex justify-between items-center font-medium">
                      <span className="text-blue-800">Total Annual Fees:</span>
                      <span className="text-blue-800">
                        ₹{formData.termFees.reduce((sum, term) => sum + (term.amount || 0), 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Fixed Action Buttons */}
          <div className="flex justify-end space-x-4 p-6 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={() => navigate('/classes')}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-8 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 active:scale-95"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <span>Update Class</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClassEdit;
